import React, { useMemo, useState } from "react";
import {
  getCanonicalShiftStatus,
  getAssignedWorkerUid,
  isOpenDemandShiftStatus,
  isActiveShiftStatus,
} from "../../../core/shifts/shiftLifecycle";
import {
  buildFacility360Metrics,
  getFacility360ShiftStart,
  getFacility360ShiftEnd,
  getFacility360ShiftHours,
  getFacility360ShiftRole,
  facility360ToDate,
} from "../utils/facility360Metrics";

function normalize(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
}

function titleCase(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) =>
      letter.toUpperCase()
    );
}


function getWorkerName(
  shift,
  users = []
) {
  const directName =
    shift?.assignedWorkerName ||
    shift?.workerName ||
    shift?.assignedWorkerDisplayName;

  if (directName) {
    return directName;
  }

  const workerUid =
    getAssignedWorkerUid(shift);

  if (!workerUid) {
    return "Unassigned";
  }

  const worker = (
    Array.isArray(users) ? users : []
  ).find((user) => {
    const userUid = String(
      user?.uid || user?.id || ""
    ).trim();

    return userUid === workerUid;
  });

  if (!worker) {
    return "Assigned Worker";
  }

  return (
    worker?.displayName ||
    worker?.fullName ||
    [
      worker?.firstName,
      worker?.lastName,
    ]
      .filter(Boolean)
      .join(" ") ||
    worker?.email ||
    "Assigned Worker"
  );
}

function formatDate(value) {
  const date = facility360ToDate(value);

  if (!date) return "Date unavailable";

  return date.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatTime(value) {
 const date = facility360ToDate(value);

  if (!date) return "—";

  return date.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
}

function MetricCard({
  label,
  value,
  color = "#0f172a",
}) {
  return (
    <div
      style={{
        padding: 13,
        borderRadius: 14,
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
      }}
    >
      <div
        style={{
          color: "#64748b",
          fontSize: 10,
          fontWeight: 900,
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>

      <div
        style={{
          marginTop: 6,
          color,
          fontSize: 21,
          fontWeight: 950,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function getStatusTone(status) {
  const normalizedStatus =
    normalize(status);

  if (normalizedStatus === "completed") {
    return {
      color: "#166534",
      background: "#dcfce7",
    };
  }

  if (
    [
      "cancelled",
      "canceled",
      "no_show",
      "closed_unfilled",
    ].includes(normalizedStatus)
  ) {
    return {
      color: "#991b1b",
      background: "#fee2e2",
    };
  }

  if (
    isOpenDemandShiftStatus(
      normalizedStatus
    )
  ) {
    return {
      color: "#92400e",
      background: "#fef3c7",
    };
  }

  return {
    color: "#1d4ed8",
    background: "#dbeafe",
  };
}

export default function Facility360Shifts({
  facility,
  shifts = [],
  users = [],
}) {
  const [statusFilter, setStatusFilter] =
    useState("all");

  const [roleFilter, setRoleFilter] =
    useState("all");

  const facilityId = normalize(
    facility?.id || facility?.facilityId
  );

  const sharedMetrics = useMemo(
    () =>
      buildFacility360Metrics({
        facility,
        shifts,
        users: [],
      }),
    [facility, shifts]
  );

  const {
    facilityShifts,
    openShifts: open,
    activeShifts: active,
    completedShifts: completed,
    cancelledShifts: cancelled,
    noShowShifts: noShows,
    coverage,
    completedHours,
  } = sharedMetrics;

  const metrics = {
    open,
    active,
    completed,
    cancelled,
    noShows,
    coverage,
    completedHours,
  };

const roles = useMemo(() => {
  return Array.from(
    new Set(
      facilityShifts
        .map(getFacility360ShiftRole)
        .filter(Boolean)
    )
  ).sort();
}, [facilityShifts]);

  const filteredShifts = useMemo(() => {
    return facilityShifts.filter((shift) => {
const status =
  getCanonicalShiftStatus(shift);

const role = normalize(
  getFacility360ShiftRole(shift)
);

const matchesStatus =
  statusFilter === "all" ||

  (statusFilter === "open" &&
    isOpenDemandShiftStatus(status)) ||

  (statusFilter === "active" &&
    isActiveShiftStatus(status)) ||

  (statusFilter === "cancelled" &&
    [
      "cancelled",
      "canceled",
      "no_show",
      "closed_unfilled",
    ].includes(status)) ||

  status === statusFilter;

      const matchesRole =
        roleFilter === "all" ||
        role === normalize(roleFilter);

      return matchesStatus && matchesRole;
    });
  }, [
    facilityShifts,
    statusFilter,
    roleFilter,
  ]);

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div>
        <div
          style={{
            color: "#0f172a",
            fontSize: 20,
            fontWeight: 950,
          }}
        >
          Facility Shifts
        </div>

        <div
          style={{
            marginTop: 4,
            color: "#64748b",
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          Shift coverage and history for{" "}
          {facility?.name ||
            facility?.facilityName ||
            facility?.id ||
            "this facility"}
          .
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(2, minmax(0, 1fr))",
          gap: 9,
        }}
      >
        <MetricCard
          label="All Shifts"
          value={facilityShifts.length}
        />

        <MetricCard
          label="Coverage"
          value={`${metrics.coverage}%`}
          color="#166534"
        />

        <MetricCard
          label="Open"
          value={metrics.open.length}
          color={
            metrics.open.length > 0
              ? "#b45309"
              : "#166534"
          }
        />

        <MetricCard
          label="Active"
          value={metrics.active.length}
          color="#1d4ed8"
        />

        <MetricCard
          label="Completed"
          value={metrics.completed.length}
          color="#166534"
        />

        <MetricCard
          label="Completed Hours"
          value={metrics.completedHours.toFixed(1)}
          color="#7c3aed"
        />

        <MetricCard
          label="Cancelled"
          value={metrics.cancelled.length}
          color={
            metrics.cancelled.length > 0
              ? "#b91c1c"
              : "#166534"
          }
        />

        <MetricCard
          label="No-Shows"
          value={metrics.noShows.length}
          color={
            metrics.noShows.length > 0
              ? "#b91c1c"
              : "#166534"
          }
        />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(2, minmax(0, 1fr))",
          gap: 8,
        }}
      >
        <select
          value={statusFilter}
          onChange={(event) =>
            setStatusFilter(event.target.value)
          }
          style={{
            minHeight: 40,
            padding: "0 10px",
            borderRadius: 12,
            color: "#0f172a",
            background: "#ffffff",
            border: "1px solid #cbd5e1",
            fontWeight: 800,
          }}
        >
          <option value="all">
            All statuses
          </option>
          <option value="open">Open</option>
          <option value="active">Active</option>
          <option value="completed">
            Completed
          </option>
          <option value="cancelled">
            Cancelled / No-Show
          </option>
        </select>

        <select
          value={roleFilter}
          onChange={(event) =>
            setRoleFilter(event.target.value)
          }
          style={{
            minHeight: 40,
            padding: "0 10px",
            borderRadius: 12,
            color: "#0f172a",
            background: "#ffffff",
            border: "1px solid #cbd5e1",
            fontWeight: 800,
          }}
        >
          <option value="all">All roles</option>

          {roles.map((role) => (
            <option key={role} value={role}>
              {role}
            </option>
          ))}
        </select>
      </div>

      {filteredShifts.length === 0 ? (
        <div
          style={{
            padding: 20,
            borderRadius: 14,
            textAlign: "center",
            color: "#64748b",
            background: "#f8fafc",
            border: "1px dashed #cbd5e1",
            fontWeight: 750,
          }}
        >
          No matching shifts were found.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 9 }}>
          {filteredShifts.map((shift, index) => {
            const start =
              getFacility360ShiftStart(shift);

            const end =
              getFacility360ShiftEnd(shift);
            const status =
              getCanonicalShiftStatus(shift) ||
              "unknown";
            const tone = getStatusTone(status);

            return (
              <div
                key={
                  shift.id ||
                  shift.shiftId ||
                  `${facilityId}-${index}`
                }
                style={{
                  padding: 13,
                  borderRadius: 14,
                  background: "#ffffff",
                  border:
                    "1px solid #e2e8f0",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent:
                      "space-between",
                    alignItems: "flex-start",
                    gap: 10,
                  }}
                >
                  <div>
                    <div
                      style={{
                        color: "#0f172a",
                        fontSize: 14,
                        fontWeight: 950,
                      }}
                    >
                      {getFacility360ShiftRole(shift)}
                    </div>

                    <div
                      style={{
                        marginTop: 4,
                        color: "#64748b",
                        fontSize: 12,
                        fontWeight: 750,
                      }}
                    >
                      {formatDate(start)}
                    </div>

                    <div
                      style={{
                        marginTop: 3,
                        color: "#475569",
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {formatTime(start)} –{" "}
                      {formatTime(end)}
                      {" · "}
                    {getFacility360ShiftHours(shift).toFixed(1)}{" "}
                      hours
                    </div>

                    <div
                      style={{
                        marginTop: 5,
                        color: "#334155",
                        fontSize: 11,
                        fontWeight: 800,
                      }}
                    >
                     Worker: {getWorkerName(shift, users)} 
                    </div>
                  </div>

                  <div
                    style={{
                      padding: "6px 9px",
                      borderRadius: 999,
                      color: tone.color,
                      background: tone.background,
                      fontSize: 9,
                      fontWeight: 950,
                      whiteSpace: "nowrap",
                      textTransform: "uppercase",
                    }}
                  >
                    {titleCase(status)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}